import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Dialog,
  Stack,
  Text,
  useToast,
} from 'braid-design-system';
import type { FC } from 'react';

import type { PageConfig } from 'src/libs/PageConfig';

import { TagsList } from '../TagsList';

import translations from './.vocab';
import { BulkEmailForm } from './BulkEmailForm';

interface BulkEmailDialogProps {
  open: boolean;
  onClose: () => void;
  getSelectedJobApplicationNames: () => string[];
  jobTitle: string;
  advertiserName: string;
  userName: string;
  userEmail: string;
  sendProspectEmail: (props: {
    subject: string;
    message: string;
    enableReplies: boolean;
    language: Language;
    addCustomError: PageConfig['dataDogRum']['addCustomError'];
  }) => Promise<boolean>;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

export const BulkEmailDialog: FC<BulkEmailDialogProps> = ({
  open,
  onClose,
  getSelectedJobApplicationNames,
  advertiserName,
  jobTitle,
  sendProspectEmail,
  userEmail,
  userName,
  addCustomError,
}) => {
  const { t } = useTranslations(translations);
  const showToast = useToast();

  const selectedJobApplicationNames = getSelectedJobApplicationNames();

  return (
    <Dialog
      id="bulk-email-modal"
      title={t('Send email')}
      open={open}
      onClose={onClose}
      width="medium"
      data={{ testid: 'bulk-email-modal' }}
    >
      <Columns space="small">
        <Column width="2/3">
          <BulkEmailForm
            onCancel={onClose}
            closeAndShowSuccessMessage={(message, description, action) => {
              showToast({
                message,
                description,
                tone: 'positive',
                action,
                closeLabel: t('Close'),
              });
              onClose();
            }}
            selectedJobApplicationsCount={selectedJobApplicationNames.length}
            jobTitle={jobTitle}
            advertiserName={advertiserName}
            userName={userName}
            userEmail={userEmail}
            sendProspectEmail={sendProspectEmail}
            addCustomError={addCustomError}
          />
        </Column>
        <Column width="1/3">
          <Stack space="small">
            <Text weight="strong">
              {t('Email candidate: labelCount', {
                labelCount: selectedJobApplicationNames.length,
              })}
            </Text>
            <Box overflow="auto" style={{ maxHeight: '60vh' }}>
              <TagsList names={selectedJobApplicationNames} />
            </Box>
          </Stack>
        </Column>
      </Columns>
    </Dialog>
  );
};
